/**
 * MailChimp Integration Dashboard Component
 * Main dashboard for MailChimp integration management
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNotifications } from '../common/NotificationSystem';
import { mailchimpService } from '../../services/mailchimpService';
import { useRBAC } from '../../hooks/useRBAC';
import ProfessionalLoader from "../common/ProfessionalLoader";
import MailChimpCampaigns from './MailChimpCampaigns';
import MailChimpLists from './MailChimpLists';
import MailChimpSyncs from './MailChimpSyncs';
import './MailChimpIntegrationDashboard.css';

const MailChimpIntegrationDashboard = () => {
  const { showSuccess, showError } = useNotifications();
  const { canManageIntegrations } = useRBAC();

  // State management
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // Connection status state (independent of dashboard data)
  const [connectionStatus, setConnectionStatus] = useState({
    isConnected: false,
    apiStatus: 'unknown',
    lastTested: null,
    isTesting: false
  });

  // Load dashboard data
  const loadDashboardData = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await mailchimpService.getMailChimpDashboard();

      if (response.success) {
        setDashboardData(response.data);
      } else {
        showError(response.message || 'Failed to load dashboard data', 'Error');
      }
    } catch (error) {
      showError('Failed to load dashboard data', 'Error');
    } finally {
      setIsLoading(false);
    }
  }, [showError]);

  // Load dashboard data on component mount
  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // Handle test connection
  const handleTestConnection = async () => {
    setIsProcessing(true);
    setConnectionStatus(prev => ({ ...prev, isTesting: true }));

    try {
      const response = await mailchimpService.testConnection();

      if (response.success) {
        showSuccess('MailChimp connection successful', 'Success');

        // Update connection status state immediately
        setConnectionStatus({
          isConnected: true,
          apiStatus: 'operational',
          lastTested: new Date().toISOString(),
          isTesting: false
        });
      } else {
        showError(response.message || 'Connection test failed', 'Error');

        // Update connection status state for failure
        setConnectionStatus({
          isConnected: false,
          apiStatus: 'error',
          lastTested: new Date().toISOString(),
          isTesting: false
        });
      }
    } catch (error) {
      showError('Connection test failed', 'Error');

      // Update connection status state for error
      setConnectionStatus({
        isConnected: false,
        apiStatus: 'error',
        lastTested: new Date().toISOString(),
        isTesting: false
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle sync all members
  const handleSyncAllMembers = async () => {
    setIsProcessing(true);
    try {
      const response = await mailchimpService.syncAllMembers();

      if (response.success) {
        showSuccess('All members synced successfully', 'Success');
        loadDashboardData();
      } else {
        showError(response.message || 'Sync failed', 'Error');
      }
    } catch (error) {
      showError('Sync failed', 'Error');
    } finally {
      setIsProcessing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="mailchimp-integration-dashboard">
        <div className="loading-container">
          <ProfessionalLoader size="large" variant="default" />
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="mailchimp-integration-dashboard">
        <div className="error-state">
          <h3>Failed to Load Dashboard</h3>
          <p>Unable to load MailChimp integration dashboard data.</p>
          <button className="btn btn-primary" onClick={loadDashboardData}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  const {
    statistics = {},
    recent_syncs = [],
    failed_syncs = [],
  } = dashboardData;

  return (
    <div className="mailchimp-integration-dashboard">
      {/* Header */}
      <div className="mailchimp-integration-dashboard-header">
        <div className="header-content">
          <h2>📧 MailChimp Integration Dashboard</h2>
          <p>Manage MailChimp campaigns, lists, and member synchronization</p>
        </div>
        <div className="header-actions">
          <button
            className="btn btn-outline"
            onClick={handleTestConnection}
            disabled={isProcessing || !canManageIntegrations}
          >
            {isProcessing ? 'Testing...' : 'Test Connection'}
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSyncAllMembers}
            disabled={isProcessing || !canManageIntegrations}
          >
            {isProcessing ? 'Syncing...' : 'Sync All Members'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="dashboard-tabs">
        <button
          className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          📊 Overview
        </button>
        <button
          className={`tab-button ${activeTab === 'campaigns' ? 'active' : ''}`}
          onClick={() => setActiveTab('campaigns')}
        >
          📧 Campaigns
        </button>
        <button
          className={`tab-button ${activeTab === 'lists' ? 'active' : ''}`}
          onClick={() => setActiveTab('lists')}
        >
          📋 Lists
        </button>
        <button
          className={`tab-button ${activeTab === 'syncs' ? 'active' : ''}`}
          onClick={() => setActiveTab('syncs')}
        >
          🔄 Syncs
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'overview' && (
          <div className="overview-tab">
            {/* Connection Status */}
            <div className="connection-section">
              <h3>Connection Status</h3>
              <div className="connection-cards">
                <div className={`connection-card ${connectionStatus.isConnected ? 'connected' : 'disconnected'}`}>
                  <div className="connection-icon">
                    {connectionStatus.isTesting ? '🔄' : connectionStatus.isConnected ? '✅' : '❌'}
                  </div>
                  <div className="connection-content">
                    <h4>API Connection</h4>
                    <p>
                      {connectionStatus.isTesting ? 'Testing...' :
                        connectionStatus.isConnected ? 'Connected' : 'Disconnected'}
                    </p>
                    <span className="connection-detail">
                      Last tested: {connectionStatus.lastTested ?
                        new Date(connectionStatus.lastTested).toLocaleString() :
                        'Never'
                      }
                    </span>
                  </div>
                </div>

                <div className={`connection-card ${connectionStatus.apiStatus === 'operational' ? 'operational' : 'error'}`}>
                  <div className="connection-icon">
                    {connectionStatus.isTesting ? '🔄' :
                      connectionStatus.apiStatus === 'operational' ? '🟢' :
                        connectionStatus.apiStatus === 'error' ? '🔴' : '🟡'}
                  </div>
                  <div className="connection-content">
                    <h4>API Status</h4>
                    <p>
                      {connectionStatus.isTesting ? 'Testing...' :
                        connectionStatus.apiStatus === 'operational' ? 'Operational' :
                          connectionStatus.apiStatus === 'error' ? 'Error' : 'Unknown'}
                    </p>
                    <span className="status-detail">
                      {connectionStatus.isTesting ? 'Testing connection...' :
                        connectionStatus.apiStatus === 'operational' ? 'All systems operational' :
                          connectionStatus.apiStatus === 'error' ? 'Connection issues detected' :
                            'Status unknown - test connection'}
                    </span>
                  </div>
                </div>
              </div>
            </div>


            {/* Statistics */}
            {dashboardData && (
              <div className="statistics-section">
                <h3>Member Statistics</h3>
                <div className="stats-grid">
                  <div className="stat-card">
                    <div className="stat-icon">👥</div>
                    <div className="stat-content">
                      <h4>Total Members</h4>
                      <p className="stat-value">{statistics.total_members || 0}</p>
                    </div>
                  </div>

                  <div className="stat-card synced">
                    <div className="stat-icon">✅</div>
                    <div className="stat-content">
                      <h4>Synced</h4>
                      <p className="stat-value">{statistics.synced_members || 0}</p>
                    </div>
                  </div>

                  <div className="stat-card pending">
                    <div className="stat-icon">⏳</div>
                    <div className="stat-content">
                      <h4>Pending</h4>
                      <p className="stat-value">{statistics.pending_members || 0}</p>
                    </div>
                  </div>

                  <div className="stat-card failed">
                    <div className="stat-icon">❌</div>
                    <div className="stat-content">
                      <h4>Failed</h4>
                      <p className="stat-value">{statistics.failed_members || 0}</p>
                    </div>
                  </div>

                  <div className="stat-card subscribed">
                    <div className="stat-icon">📧</div>
                    <div className="stat-content">
                      <h4>Subscribed</h4>
                      <p className="stat-value">{statistics.subscribed_members || 0}</p>
                    </div>
                  </div>

                  <div className="stat-card unsubscribed">
                    <div className="stat-icon">🚫</div>
                    <div className="stat-content">
                      <h4>Unsubscribed</h4>
                      <p className="stat-value">{statistics.unsubscribed_members || 0}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Recent Syncs */}
            {dashboardData && (
              <div className="recent-syncs-section">
                <h3>Recent Syncs</h3>
                <div className="syncs-list">
                  {recent_syncs.length > 0 ? (
                    recent_syncs.map(sync => (
                      <div key={sync.id} className="sync-item">
                        <div className="sync-info">
                          <span className="sync-organization">{sync.organization_name}</span>
                          <span className={`sync-status ${sync.mailchimp_sync_status}`}>
                            {sync.mailchimp_sync_status}
                          </span>
                        </div>
                        <div className="sync-time">
                          {sync.mailchimp_synced_at ?
                            new Date(sync.mailchimp_synced_at).toLocaleString() :
                            'Never'
                          }
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="no-syncs">
                      <p>No recent syncs found</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Failed Syncs */}
            {dashboardData && failed_syncs.length > 0 && (
              <div className="failed-syncs-section">
                <h3>Failed Syncs</h3>
                <div className="failed-syncs-list">
                  {failed_syncs.map(sync => (
                    <div key={sync.id} className="failed-sync-item">
                      <div className="failed-sync-info">
                        <span className="failed-sync-organization">{sync.organization_name}</span>
                        <span className="failed-sync-error">{sync.mailchimp_sync_error}</span>
                      </div>
                      <div className="failed-sync-time">
                        {sync.mailchimp_last_updated ?
                          new Date(sync.mailchimp_last_updated).toLocaleString() :
                          'Never'
                        }
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'campaigns' && (
          <MailChimpCampaigns />
        )}

        {activeTab === 'lists' && (
          <MailChimpLists />
        )}

        {activeTab === 'syncs' && (
          <MailChimpSyncs />
        )}
      </div>
    </div>
  );
};

export default MailChimpIntegrationDashboard;
